import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { hot } from 'react-hot-loader';
import { SessionProvider, FlagProvider } from '@cordata/buffet';
import { ToastContainer, toast } from 'react-toastify';

import { Paths } from '~/components';
import ThemeProvider from '~/utils/theme/context';
import { SlideOutProvider } from '~/components/common/SlideOut';
import { PatientSearchProvider } from '~/utils/patient/search/context';
import { BreakpointProvider } from '~/utils/breakpoint';
import { SessionTimeoutProvider } from '~/utils/session-timeout';

import { SubscriptionProvider } from '~/utils/subscription';

const channels = [{
  name: 'subscriptions',
  on: ['new_message'],
}];

const ContextProviderWrapper = () => {
  const location = useLocation();
  const history = useHistory();

  const userInPrivateRoutes = () =>
    location.pathname !== '/' &&
      location.pathname !== '/hwc' &&
      !location.pathname.startsWith('/cit') &&
      !location.pathname.startsWith('/session-timeout') &&
      !location.pathname.includes('login') &&
      !location.pathname.startsWith('/ext');

  useEffect(() => {
    const routeAttempted = localStorage.getItem('route-attempted');
    const ra = JSON.parse(routeAttempted || '{}');

    if (routeAttempted && location.pathname !== ra.pathname && userInPrivateRoutes()) {
      toast.info('Redirecting to previously attempted page ...');

      localStorage.removeItem('route-attempted');

      history.replace(ra.pathname, ra.state);
    }
  }, []);

  return (
    <>
      <SessionProvider userInPrivateRoutes={userInPrivateRoutes}>
        <SessionTimeoutProvider>
          <BreakpointProvider>
            <ThemeProvider>
              <FlagProvider>
                <SubscriptionProvider
                  channels={channels}
                >
                  <SlideOutProvider>
                    <PatientSearchProvider>
                      <Paths />
                    </PatientSearchProvider>
                  </SlideOutProvider>
                </SubscriptionProvider>
              </FlagProvider>
            </ThemeProvider>
          </BreakpointProvider>
        </SessionTimeoutProvider>
      </SessionProvider>

      <ToastContainer
        position="bottom-center"
        autoClose={2500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
};

type tGetUserConfirmation = (
  message: string,
  callback: (allow: boolean) => void,
) => void;

const getUserConfirmation: tGetUserConfirmation = (
  message,
  callback,
) => {
  const allowTransition = window.confirm(message);

  callback(allowTransition);
};

const App = () => (
  <Router
    getUserConfirmation={getUserConfirmation}
  >
    <ContextProviderWrapper />
  </Router>
);

export default hot(module)(App);
